"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Building2, Check, ChevronDown, Plus } from "lucide-react";
import type { FormState } from "@/modules/shared/forms";

/**
 * The clinic switcher (H37, docs/ux/flows/business-clinic-switching.md).
 *
 * Rendered only for a member who holds a Membership at more than one Business,
 * which is almost nobody: with one clinic the business chrome is exactly what
 * it was before multi-membership existed, and that is what makes the change
 * invisible to a business that will never use it.
 *
 * **Every row is a submit button, not a link.** Switching writes server-side
 * state on the member's Account, and a link that writes is one prefetch away
 * from moving somebody's clinic while they hover over the menu. One form wraps
 * the whole list and each row carries its own `businessId` as the submitting
 * button's value, so the list needs one action state rather than N.
 *
 * **The panel is portalled to the body**, like the notification bell beside it
 * and for the same reason: the header uses `backdrop-filter`, which makes it a
 * containing block for `position: fixed` descendants, so a panel rendered in
 * place is positioned against the 64px header instead of the viewport
 * (design-system.md §12).
 *
 * **Shared by both frontends since R22 T14**, with the switch command and the
 * link injected. The part worth sharing is not the menu, it is the three
 * paragraphs of hard-won behaviour: rows are buttons rather than links, the
 * panel is portalled, and a successful switch does a **document navigation**.
 */

const PANEL_WIDTH = 288; // 18rem
const PANEL_MARGIN = 8;

export interface ClinicOption {
  businessId: string;
  name: string;
}

export function ClinicSwitcher({
  clinics,
  activeBusinessId,
  switchTo,
  Link,
}: {
  clinics: ClinicOption[];
  activeBusinessId: string;
  /** Write the member's active clinic. A `success` token means it changed. */
  switchTo: (businessId: string) => Promise<FormState>;
  Link: (props: {
    href: string;
    className?: string;
    onClick?: () => void;
    children: ReactNode;
  }) => ReactNode;
}) {
  const [state, setState] = useState<FormState>({});
  const [pending, setPending] = useState(false);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [placement, setPlacement] = useState<CSSProperties | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setPlacement(null);
  }, []);

  /**
   * The client half of the switch, and it is load-bearing rather than tidying.
   *
   * **A document navigation, deliberately, after three softer things failed.**
   * A server `redirect`, then `revalidatePath("/business", "layout")`, then
   * `router.push` + `router.refresh()` each left the Router Cache replaying the
   * `/business` payload rendered for the clinic being **left**: the header said
   * one clinic and the dashboard under it belonged to the other. That is the
   * worst possible failure for this feature, because everything on the page is
   * believable and wrong, so the fix is the one that cannot be subtly defeated.
   *
   * It costs a full page load, on an action somebody takes a few times a day at
   * most, and it throws away every cached fragment of the clinic being left,
   * which is precisely what is wanted here.
   *
   * `/business` and not "stay where you are": several business screens are
   * addressed by an id belonging to the clinic being left, and
   * `/business/schedule/[practitionerId]` would render a not-found for a
   * practitioner the member can no longer see.
   */
  useEffect(() => {
    if (!state.success) return;
    window.location.assign("/business");
  }, [state]);

  const choose = (businessId: string) => {
    if (businessId === activeBusinessId) {
      close();
      return;
    }
    setPending(true);
    setState({});
    void switchTo(businessId)
      .then(setState)
      .finally(() => setPending(false));
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const element = triggerRef.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      const untrimmed = rect.right - PANEL_WIDTH;
      const maxLeft = window.innerWidth - PANEL_WIDTH - PANEL_MARGIN;
      setPlacement({
        position: "fixed",
        top: rect.bottom + 8,
        left: Math.max(PANEL_MARGIN, Math.min(untrimmed, maxLeft)),
        width: PANEL_WIDTH,
        maxHeight: "min(24rem, 70vh)",
      });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open]);

  const active = clinics.find((c) => c.businessId === activeBusinessId);
  const activeName = active?.name ?? "Your clinic";

  return (
    <div className="shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Working at ${activeName}. Switch clinic`}
        onClick={() => (open ? close() : setOpen(true))}
        className="inline-flex h-10 max-w-[9rem] items-center gap-1.5 rounded-lg border border-line bg-surface px-2.5 text-sm font-semibold text-ink transition-colors hover:bg-page sm:max-w-[14rem]"
      >
        <Building2 className="size-4 shrink-0 text-muted" aria-hidden strokeWidth={1.8} />
        <span className="truncate">{activeName}</span>
        <ChevronDown className="size-4 shrink-0 text-muted" aria-hidden strokeWidth={1.8} />
      </button>

      {open &&
        placement !== null &&
        createPortal(
          <>
            <button
              type="button"
              aria-label="Close clinic menu"
              onClick={close}
              className="fixed inset-0 z-40 bg-transparent"
            />
            <div
              role="menu"
              aria-label="Your clinics"
              style={placement}
              className="z-50 overflow-y-auto rounded-2xl border border-line bg-surface shadow-[var(--shadow-lift)]"
            >
              <p className="border-b border-line px-4 py-2 text-xs font-bold uppercase tracking-wide text-muted">
                Your clinics
              </p>

              <ul className="divide-y divide-line">
                  {clinics.map((clinic) => {
                    const isActive = clinic.businessId === activeBusinessId;
                    return (
                      <li key={clinic.businessId}>
                        <button
                          type="button"
                          onClick={() => choose(clinic.businessId)}
                          disabled={pending}
                          role="menuitemradio"
                          aria-checked={isActive}
                          className="flex w-full items-center gap-2 px-4 py-3 text-start text-sm font-semibold text-ink transition-colors hover:bg-page disabled:opacity-60"
                        >
                          <Check
                            className={`size-4 shrink-0 ${isActive ? "text-accent" : "text-transparent"}`}
                            aria-hidden
                            strokeWidth={2}
                          />
                          <span className="min-w-0 flex-1 truncate">{clinic.name}</span>
                          {isActive && <span className="sr-only">Current clinic</span>}
                        </button>
                      </li>
                    );
                  })}
              </ul>

              {state.formError && (
                <p
                  role="alert"
                  className="border-t border-line bg-danger/10 px-4 py-3 text-sm text-danger"
                >
                  {state.formError}
                </p>
              )}

              <div className="border-t border-line">
                <Link
                  href="/join/business"
                  onClick={close}
                  className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-accent transition-colors hover:bg-page"
                >
                  <Plus className="size-4 shrink-0" aria-hidden strokeWidth={2} />
                  Register another clinic
                </Link>
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
